import {FlowsQueryEventEntry} from 'common/widgets/query-entry-section/types';
import {FullLengthDateRange} from 'common/widgets/date-range-picker/models/full-length-date-range';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {SavedCohortSegment} from 'common/widgets/query-block/segment-menu/types';
import {Expression} from 'common/widgets/property-filter-menu/models/selector-tree/types';
import {Alignment} from 'common/widgets/sankey-chart/models/types';
import {BookmarkDateRange} from 'common/widgets/date-range-picker/models/types';
import {EventItem} from 'common/widgets/query-block/events-menu/types';
import {PropertyItem} from 'common/widgets/query-block/properties-menu/types';

import {AggregateBy, BookmarkFunnelExclusionStep, BookmarkFunnelStep, FunnelBookmark} from '../funnels/types';

export enum ChartType {
  Sankey = `sankey`,
  Paths = `paths`,
}

export const ChartTypeLabels = {
  [ChartType.Sankey]: `Paths`,
  [ChartType.Paths]: `Top paths`,
};

export const ChartTypeIcon: {[key in ChartType]: string} = {
  [ChartType.Paths]: `funnel-steps`,
  [ChartType.Sankey]: `top-paths`,
};

export enum MergeType {
  Graph = `graph`,
  List = `list`,
}

export enum FlowDirection {
  Forward = `forward`,
  Reverse = `reverse`,
}

export enum FlowAnchorPosition {
  Funnel = `funnel`,
  Step = `step`,
}

export enum AnchorPosition {
  Funnel,
  StepAndFunnel,
}

export const CHART_TYPE_TO_QUERY_PARAM = {
  [ChartType.Sankey]: MergeType.Graph,
  [ChartType.Paths]: MergeType.List,
};

export interface AnchorsFlowsReport {
  anchors: Array<{
    event: FlowsQueryEventEntry;
    forward?: number;
    reverse?: number;
  }>;
  dateRange: FullLengthDateRange;
  filterByCohort: SavedCohortSegment['cohort'];
  filters: PropertyFilter[];
  groupByCohorts: Array<SavedCohortSegment['cohort']>;
}

export interface AnchorsFlowsReportQueryParams {
  anchors: Array<{
    event: FlowsQueryEventEntry;
    forward?: number;
    reverse?: number;
  }>;
  filter_by_cohort: UnknownObj | SavedCohortSegment['cohort'];
  filter: Expression;
  from_date?: string;
  group_by_cohorts: Array<SavedCohortSegment['cohort']>;
  to_date?: string;
  time_percentiles: number[];
}

export const AnchorsFlowsReportDefaults = {
  anchors: [],
  dateRange: new FullLengthDateRange(),
  filters: [],
  filterByCohort: null,
  groupByCohorts: [],
};

export type FunnelParamsForFlows = {
  funnelBookmark: FunnelBookmark;
  funnelSelectedStep?: FunnelSelectedStep;
};

export type FunnelSelectedStep = {
  stepIndex: number;
  // segments from the selected step when there is a breakdown, deliminated by " / "
  segments?: string;
};

export type FunnelContextSentence = {
  countType: string;
  funnelOrder: string;
  conversionWindow: string;
  numHoldingConstant: number;
  numFilters: number;
};

export type FlowsBookmark = {
  aggregate_by?: AggregateBy[];
  alignment?: Alignment[];
  anchorPosition?: string;
  anchor_position?: number;
  cardinality?: number;
  cardinality_threshold: number;
  chartType?: ChartType;
  count_type?: string;
  collapse_repeated?: boolean;
  data_group_id?: string;
  date_range: BookmarkDateRange;
  exclude_everything_else_user_segment?: string;
  exclusions: BookmarkFunnelExclusionStep[];
  filter_by_cohort: string;
  filter_by_event?: {
    operator: string;
    children: Array<
      | {
          event: PropertyFilter;
        }
      | {
          user: PropertyFilter;
        }
      | {
          other: PropertyFilter;
        }
    >;
  };
  flows_merge_type: MergeType;
  funnelParams?: FunnelParamsForFlows;
  graph_sort_priority?: string;
  group_by?: TODO[];
  hidden_events?: string[];
  list_sort_priority?: string;
  maxResults?: number;
  maxSteps?: number;
  reverse?: boolean;
  segments: Array<{
    event: EventItem;
    property: PropertyItem;
  }>;
  show_custom_events?: boolean;
  steps: BookmarkFunnelStep[];
  time_percentiles_enabled?: boolean;
  version: number;
  visible_events?: string[];
};
